import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Sparkles, Ruler, Boxes, Truck } from 'lucide-react';
import { Reveal, ProductCard } from '../site/Kit';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const FeaturedProducts = ({ c }) => {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    axios.get(`${API_URL}/api/products`).then(({ data }) => setProducts(data.slice(0, 4))).catch(() => {});
  }, []);
  if (!products.length) return null;
  return (
    <section className="px-4 sm:px-6 lg:px-8 py-24" data-testid="featured-section">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-end mb-10">
          <div>
            <p className="k-label mb-3">{c.featured_label}</p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#111]">{c.featured_title}</h2>
          </div>
          <Link to="/catalogo" className="text-sm font-semibold text-[#1b3352] flex items-center gap-1 hover:gap-2 transition-[gap]" data-testid="featured-see-all">
            Ver todo <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.map((p, i) => <Reveal key={p.product_id} delay={i * 0.08}><ProductCard product={p} /></Reveal>)}
        </div>
      </div>
    </section>
  );
};

const STEP_ICONS = [Sparkles, Ruler, Boxes, Truck];

export const MadeToOrder = ({ c }) => (
  <section className="px-4 sm:px-6 lg:px-8 py-24 bg-[#eef0f2]" data-testid="made-section">
    <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
      <Reveal>
        <p className="k-label mb-4">{c.made_label}</p>
        <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#111] leading-tight mb-6">{c.made_title}</h2>
        <p className="text-base md:text-lg text-[#5f6672] leading-relaxed mb-8 max-w-md">{c.made_text}</p>
        <Link to="/personaliza" className="k-btn-primary" data-testid="cta-button">{c.made_btn} <ArrowRight className="w-4 h-4" /></Link>
      </Reveal>
      <div className="grid grid-cols-2 gap-4">
        {[1, 2, 3, 4].map((n, i) => {
          const Icon = STEP_ICONS[i];
          return (
            <Reveal key={n} delay={i * 0.1} className={i % 2 ? 'translate-y-6' : ''}>
              <div className="bg-white rounded-xl p-6 h-full shadow-[0_10px_30px_rgba(17,17,17,0.04)]" data-testid={`how-we-work-step-${n}`}>
                <Icon className="w-5 h-5 text-[#7a5a2e] mb-4" />
                <h3 className="font-display font-semibold text-[#111] mb-2">{c[`step_${n}_title`]}</h3>
                <p className="text-sm text-[#5f6672] leading-relaxed">{c[`step_${n}_text`]}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

export const AboutBlock = ({ c }) => (
  <section className="px-4 sm:px-6 lg:px-8 py-24 bg-white" data-testid="about-section">
    <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
      <Reveal><img src={c.home_about_image} alt="" className="w-full h-[440px] object-cover rounded-2xl" data-testid="about-image" /></Reveal>
      <Reveal delay={0.1}>
        <p className="k-label mb-4">{c.home_about_label}</p>
        <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#111] leading-tight mb-6">{c.home_about_title}</h2>
        <p className="text-base md:text-lg text-[#5f6672] leading-relaxed mb-8">{c.home_about_text}</p>
        <Link to="/sobre-mi" className="k-btn-ghost" data-testid="about-cta">{c.home_about_btn} <ArrowRight className="w-4 h-4" /></Link>
      </Reveal>
    </div>
  </section>
);

export const GalleryPreview = ({ c }) => {
  const items = (c.gallery_items || []).slice(0, 6);
  if (!items.length) return null;
  return (
    <section className="px-4 sm:px-6 lg:px-8 py-24" data-testid="gallery-preview-section">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-end mb-10">
          <div>
            <p className="k-label mb-3">{c.home_gallery_label}</p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#111]">{c.home_gallery_title}</h2>
          </div>
          <Link to="/galeria" className="text-sm font-semibold text-[#1b3352] flex items-center gap-1 hover:gap-2 transition-[gap]" data-testid="gallery-see-all">
            Ver galería <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {items.map((it, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <div className="aspect-square overflow-hidden rounded-xl group" data-testid={`gallery-preview-${i}`}>
                <img src={it.image} alt={it.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export const FinalCta = ({ title, btn, testId = 'final-cta' }) => (
  <section className="px-4 sm:px-6 lg:px-8 py-24 bg-[#1b3352]" data-testid={testId}>
    <Reveal className="max-w-4xl mx-auto text-center">
      <h2 className="font-display font-bold text-3xl sm:text-5xl text-white leading-tight mb-10">{title}</h2>
      <Link to="/personaliza" className="inline-flex items-center gap-2 px-7 py-4 rounded-md bg-white text-[#1b3352] font-semibold hover:-translate-y-0.5 transition-transform" data-testid={`${testId}-button`}>
        {btn} <ArrowRight className="w-4 h-4" />
      </Link>
    </Reveal>
  </section>
);
